import { useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { Check, Copy, Eye, KeyRound, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PageContainer } from '@/components/page-container';
import { useCurrentRole } from '@/hooks/use-current-role';
import { useFetch } from '@/hooks/use-fetch';
import { docsUrl } from '@/lib/docs-url';
import { orgUrl } from '@/lib/org-url';
import { CreateApiKeyDialog } from './create-api-key-dialog';
import { useMyApiKeys, useRevokeApiKey, type ApiKeyRow } from './org-api-queries';


function keyLabel(row: ApiKeyRow): string {
  if (row.name) return row.name;
  const created = new Date(row.createdAt).toLocaleDateString();
  return `API key from ${created}`;
}

function isExpired(row: ApiKeyRow): boolean {
  return row.expiresAt !== null && new Date(row.expiresAt).getTime() <= Date.now();
}

// Expiry instants are pinned to end-of-day UTC-12 (see backend mintForUser), so
// rendering in that zone recovers the calendar date the user picked — a plain
// toLocaleDateString() would show the next day in zones west of UTC-12's date.
function expiryDateLabel(expiresAt: string): string {
  return new Date(expiresAt).toLocaleDateString(undefined, { timeZone: 'Etc/GMT+12' });
}

function RevealKeyDialog({ publicId }: { publicId: string }) {
  const fetchWithAuth = useFetch();
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setToken(null);
      setCopied(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchWithAuth(`/agent/credentials/${publicId}/reveal`);
      const body = (await res.json()) as { token?: string };
      if (body.token) setToken(body.token);
    } catch {
      toast.error('Failed to reveal the key');
    } finally {
      setLoading(false);
    }
  }

  async function copy() {
    if (!token) return;
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger
        render={
          <Button variant="ghost" size="sm">
            <Eye className="size-4" />
            Reveal
          </Button>
        }
      />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>API Key</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : token ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Input readOnly value={token} className="font-mono text-xs" />
              <Button variant="outline" size="icon" onClick={copy} aria-label="Copy key">
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
            <Button className="self-center" onClick={() => setOpen(false)}>Done</Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function RowActions({ row, orgId }: { row: ApiKeyRow; orgId: string | undefined }) {
  const revoke = useRevokeApiKey(orgId);

  return (
    <div className="flex items-center justify-end gap-2">
      {row.retrievable && !isExpired(row) && <RevealKeyDialog publicId={row.publicId} />}
      <AlertDialog>
        <AlertDialogTrigger
          render={
            <Button variant="ghost" size="sm" aria-label={`Revoke ${keyLabel(row)}`}>
              <Trash2 className="size-4" />
              Revoke
            </Button>
          }
        />
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke {keyLabel(row)}?</AlertDialogTitle>
            <AlertDialogDescription>
              Anything using this key stops working immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await revoke.mutateAsync({ publicId: row.publicId });
                  toast.success('Key revoked');
                } catch {
                  toast.error('Failed to revoke the key');
                }
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function OrgApiPage() {
  const { orgId } = useParams();
  const { role, isLoading } = useCurrentRole();
  const mine = useMyApiKeys(orgId);
  const rows = mine.data ?? [];

  // API keys are an org-admin surface. Members bounce to the org home.
  if (!isLoading && role !== 'admin') {
    return <Navigate to={orgUrl(orgId ?? '')} replace />;
  }

  return (
    <PageContainer>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">API keys</h1>
            <p className="text-sm text-muted-foreground">
              Manage API keys for programmatic access to your organization's resources.{' '}
              <a href={docsUrl('saas-mode/use-the-api')} target="_blank" rel="noreferrer" className="underline">
                Read the API docs
              </a>
            </p>
          </div>
          <CreateApiKeyDialog orgId={orgId} />
        </div>

        {rows.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
              <div className="rounded-full bg-muted p-3">
                <KeyRound className="size-6 text-muted-foreground" />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">No API keys yet</p>
                <p className="text-sm text-muted-foreground">
                  Create a key to access your organization programmatically.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table className="table-fixed max-md:min-w-[44rem]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%]">Name</TableHead>
                  <TableHead className="w-[20%]">Created</TableHead>
                  <TableHead className="w-[20%]">Expires</TableHead>
                  <TableHead className="w-[20%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.publicId}>
                    <TableCell className="font-medium">{keyLabel(row)}</TableCell>
                    <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {isExpired(row) ? (
                        <span className="font-medium text-destructive">Expired</span>
                      ) : row.expiresAt ? (
                        expiryDateLabel(row.expiresAt)
                      ) : (
                        'No expiration'
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <RowActions row={row} orgId={orgId} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </PageContainer>
  );
}
